/**
 * Client-side smart parser for the todo input field.
 * Extracts date, reminder, and section hints from natural language.
 *
 * Supported patterns:
 * - "today", "tomorrow", "yesterday"
 * - "next monday" / "this friday" (weekday names)
 * - "@SectionName" — section hint
 * - "!3pm", "!9am", "!14:30" — reminder time (interpreted as today/tomorrow)
 * - "in 1 hour", "in 30 minutes" — relative reminder
 */

export interface ParsedInput {
  /** The cleaned todo text (with chips removed) */
  text: string
  /** Parsed date or null */
  date: Date | null
  /** Human-readable reminder description (e.g. "tomorrow at 3pm") */
  reminderText: string | null
  /** The instant the reminder resolves to, ready to send as reminder_at */
  reminderAt: Date | null
  /** Section name hint (from @Name) */
  sectionHint: string | null
}

const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

function nextWeekday(targetDay: number, fromDate: Date = new Date()): Date {
  const result = new Date(fromDate)
  const currentDay = result.getDay()
  let daysAhead = targetDay - currentDay
  if (daysAhead <= 0) daysAhead += 7
  result.setDate(result.getDate() + daysAhead)
  result.setHours(0, 0, 0, 0)
  return result
}

function thisWeekday(targetDay: number, fromDate: Date = new Date()): Date {
  const result = new Date(fromDate)
  const currentDay = result.getDay()
  let daysAhead = targetDay - currentDay
  if (daysAhead < 0) daysAhead += 7
  result.setDate(result.getDate() + daysAhead)
  result.setHours(0, 0, 0, 0)
  return result
}

function parseTimeString(timeStr: string): { hours: number; minutes: number } | null {
  // "3pm" / "3am"
  const ampmMatch = timeStr.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/i)
  if (ampmMatch) {
    let hours = parseInt(ampmMatch[1], 10)
    const minutes = ampmMatch[2] ? parseInt(ampmMatch[2], 10) : 0
    const period = ampmMatch[3].toLowerCase()
    if (period === 'pm' && hours !== 12) hours += 12
    if (period === 'am' && hours === 12) hours = 0
    return { hours, minutes }
  }

  // "14:30" / "09:00"
  const militaryMatch = timeStr.match(/^(\d{1,2}):(\d{2})$/)
  if (militaryMatch) {
    return { hours: parseInt(militaryMatch[1], 10), minutes: parseInt(militaryMatch[2], 10) }
  }

  return null
}

export function parseInput(input: string): ParsedInput {
  let remaining = input.trim()
  let date: Date | null = null
  let reminderText: string | null = null
  let reminderAt: Date | null = null
  let sectionHint: string | null = null

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  // Extract @SectionName
  const sectionMatch = remaining.match(/@([A-Za-z0-9_-]+)/)
  if (sectionMatch) {
    sectionHint = sectionMatch[1]
    remaining = remaining.replace(sectionMatch[0], '').trim()
  }

  // Pull the "!3pm" chip out now but resolve it after the date is known —
  // "tomorrow call mom !3pm" must anchor to tomorrow, not today.
  const reminderBangMatch = remaining.match(/!(\d{1,2}(?::\d{2})?\s*(?:am|pm)?)/i)
  const reminderBangTime = reminderBangMatch
    ? parseTimeString(reminderBangMatch[1].replace(/\s+/, ''))
    : null
  if (reminderBangMatch && reminderBangTime) {
    remaining = remaining.replace(reminderBangMatch[0], '').trim()
  }

  // Extract date keywords — order matters: check longer patterns first
  const lowerRemaining = remaining.toLowerCase()

  if (/\btoday\b/.test(lowerRemaining)) {
    date = new Date(today)
    remaining = remaining.replace(/\btoday\b/i, '').trim()
  } else if (/\btomorrow\b/.test(lowerRemaining)) {
    date = new Date(today)
    date.setDate(date.getDate() + 1)
    remaining = remaining.replace(/\btomorrow\b/i, '').trim()
  } else if (/\byesterday\b/.test(lowerRemaining)) {
    date = new Date(today)
    date.setDate(date.getDate() - 1)
    remaining = remaining.replace(/\byesterday\b/i, '').trim()
  } else {
    // "next monday", "this friday"
    const nextWeekdayMatch = lowerRemaining.match(/\bnext\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/)
    if (nextWeekdayMatch) {
      const dayIndex = WEEKDAYS.indexOf(nextWeekdayMatch[1])
      date = nextWeekday(dayIndex)
      remaining = remaining.replace(new RegExp(`\\bnext\\s+${nextWeekdayMatch[1]}\\b`, 'i'), '').trim()
    } else {
      const thisWeekdayMatch = lowerRemaining.match(/\bthis\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/)
      if (thisWeekdayMatch) {
        const dayIndex = WEEKDAYS.indexOf(thisWeekdayMatch[1])
        date = thisWeekday(dayIndex)
        remaining = remaining.replace(new RegExp(`\\bthis\\s+${thisWeekdayMatch[1]}\\b`, 'i'), '').trim()
      } else {
        // Bare weekday name (e.g. "monday buy milk")
        const bareWeekdayMatch = remaining.match(
          new RegExp(`\\b(${WEEKDAYS.join('|')})\\b`, 'i')
        )
        if (bareWeekdayMatch) {
          const dayIndex = WEEKDAYS.indexOf(bareWeekdayMatch[1].toLowerCase())
          date = thisWeekday(dayIndex)
          remaining = remaining.replace(new RegExp(`\\b${bareWeekdayMatch[1]}\\b`, 'i'), '').trim()
        }
      }
    }
  }

  // Now that the date is settled, anchor the "!time" reminder to it.
  if (reminderBangMatch && reminderBangTime) {
    const reminderDate = date ? new Date(date) : new Date(today)
    reminderDate.setHours(reminderBangTime.hours, reminderBangTime.minutes, 0, 0)
    // Only an undated todo rolls forward: an explicit date is the user's
    // answer to "which day", even if that time has already passed there.
    if (!date && reminderDate <= new Date()) {
      reminderDate.setDate(reminderDate.getDate() + 1)
      reminderText = `tomorrow at ${reminderBangMatch[1]}`
    } else {
      reminderText = `${date ? 'on date' : 'today'} at ${reminderBangMatch[1]}`
    }
    reminderAt = reminderDate
  }

  // Extract "in X hours/minutes" relative reminders
  const relativeReminderMatch = remaining.match(/\bin\s+(\d+)\s+(hour|hours|minute|minutes|min|mins)\b/i)
  if (relativeReminderMatch && !reminderText) {
    const amount = parseInt(relativeReminderMatch[1], 10)
    const unit = relativeReminderMatch[2].toLowerCase()
    const at = new Date()
    if (unit.startsWith('hour')) at.setHours(at.getHours() + amount)
    else at.setMinutes(at.getMinutes() + amount)
    at.setSeconds(0, 0)
    reminderAt = at
    reminderText = `in ${relativeReminderMatch[1]} ${relativeReminderMatch[2]}`
    remaining = remaining.replace(relativeReminderMatch[0], '').trim()
  }

  // Clean up extra whitespace
  const text = remaining.replace(/\s+/g, ' ').trim()

  return { text: text || input.trim(), date, reminderText, reminderAt, sectionHint }
}

/** Format a Date as YYYY-MM-DD using local time (not UTC) */
export function formatDate(d: Date): string {
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/** Get today as YYYY-MM-DD */
export function todayStr(): string {
  return formatDate(new Date())
}

/** Get the Monday of the week containing the given date */
export function getWeekStart(date: Date): Date {
  const d = new Date(date)
  const day = d.getDay()
  // Adjust to Monday (0=Sun, 1=Mon ... 6=Sat)
  const diff = day === 0 ? -6 : 1 - day
  d.setDate(d.getDate() + diff)
  d.setHours(0, 0, 0, 0)
  return d
}

/** Get 7 dates (Mon–Sun) for the week containing the given date */
export function getWeekDates(date: Date): Date[] {
  const monday = getWeekStart(date)
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday)
    d.setDate(d.getDate() + i)
    return d
  })
}
